import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ProfileSetupPage from "@/pages/ProfileSetupPage";
import PendingApprovalPage from "@/pages/PendingApprovalPage";
import SelectHealthUnitPage from "@/pages/SelectHealthUnitPage";
import DashboardPage from "@/pages/DashboardPage";
import PatientsPage from "@/pages/PatientsPage";
import PatientProfilePage from "@/pages/PatientProfilePage";
import NewPatientPage from "@/pages/NewPatientPage";
import WoundDetailPage from "@/pages/WoundDetailPage";
import NewEvolutionPage from "@/pages/NewEvolutionPage";
import InterconsultationPage from "@/pages/InterconsultationPage";
import MunicipalDashboard from "@/pages/MunicipalDashboard";
import AdminPage from "@/pages/AdminPage";
import GestaoMunicipalPage from "@/pages/GestaoMunicipalPage";
import EspecialistaDashboardPage from "@/pages/EspecialistaDashboardPage";
import CadastrarGestorPage from "@/pages/CadastrarGestorPage";
import WatermarkOverlay from "@/components/WatermarkOverlay";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import RelatorioPage from "@/pages/RelatorioPage";
import DemoPage from "@/pages/DemoPage";

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-sm text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

// Roles that manage the entire municipality — don't need to select a specific health unit
const MUNICIPALITY_ROLES = ["admin", "secretario", "coordenador", "especialista"];

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  // Admin bypasses all checks
  if (user.role === "admin") return children;
  // Redirect to profile setup if profile not complete
  if (!user.profile_complete) return <Navigate to="/profile-setup" replace />;
  // Redirect to pending approval if not validated
  if (!user.is_validated) return <Navigate to="/pending-approval" replace />;
  // After approval: redirect to select health unit if not yet selected (for non-manager roles)
  if (!MUNICIPALITY_ROLES.includes(user.role) && !user.health_unit_id) {
    return <Navigate to="/select-health-unit" replace />;
  }
  return children;
}

function AppRouter() {
  const { user } = useAuth();

  return (
    <>
      {user && <WatermarkOverlay />}
      <PWAInstallPrompt />
      <OfflineIndicator />
      <Routes>
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/profile-setup" element={<ProfileSetupPage />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />
        <Route path="/select-health-unit" element={<SelectHealthUnitPage />} />
        <Route path="/relatorio/:woundId" element={<RelatorioPage />} />
        <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/patients" element={<ProtectedRoute><PatientsPage /></ProtectedRoute>} />
        <Route path="/patients/new" element={<ProtectedRoute><NewPatientPage /></ProtectedRoute>} />
        <Route path="/patients/:patientId" element={<ProtectedRoute><PatientProfilePage /></ProtectedRoute>} />
        <Route path="/wounds/:woundId" element={<ProtectedRoute><WoundDetailPage /></ProtectedRoute>} />
        <Route path="/evolutions/new" element={<ProtectedRoute><NewEvolutionPage /></ProtectedRoute>} />
        <Route path="/teleconsultation" element={<ProtectedRoute><InterconsultationPage /></ProtectedRoute>} />
        <Route path="/municipal" element={<ProtectedRoute><MunicipalDashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/gestao-municipal" element={<ProtectedRoute><GestaoMunicipalPage /></ProtectedRoute>} />
        <Route path="/gestao-municipal/cadastrar-gestor" element={<ProtectedRoute><CadastrarGestorPage /></ProtectedRoute>} />
        <Route path="/especialista" element={<ProtectedRoute><EspecialistaDashboardPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
